//
//  BloodPressureChart.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Bar,
  Line,
  ReferenceLine,
} from "recharts";

import dayjs from "dayjs";

export type BloodPressureChartData = {
  dateString: string;
  bloodPressure: number[];
  pulse: number;
};

const COLORS = Object.freeze({
  bloodPressure: "#5eaaf8",
  pulse: "#8bd687",
  reference: "#e68167",
});

const REFERENCE_DATA = Object.freeze({
  y1: 135,
  y2: 85,
});

type BloodPressureChartProps = {
  data: BloodPressureChartData[];
};

const START_DATE_TIME_STAMP = dayjs().subtract(16, "day").valueOf();
const END_DATE_TIME_STAMP = dayjs().add(2, "day").valueOf();

const BloodPressureChart = ({ data }: BloodPressureChartProps) => {
  const transformData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      dateTimestamp: dayjs(item.dateString).valueOf(),
    }));
  }, [data]);

  return (
    <ResponsiveContainer width="80%" height={250}>
      <ComposedChart data={transformData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="dateTimestamp"
          domain={[START_DATE_TIME_STAMP, END_DATE_TIME_STAMP]}
          scale="time"
          type="number"
          tick={false}
          axisLine={false}
        />
        <YAxis
          yAxisId="left"
          orientation="left"
          domain={[30, 210]}
          ticks={[30, 60, 90, 120, 150, 180, 210]}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          domain={[30, 210]}
          ticks={[85, 135]}
        />
        <ReferenceLine
          y={REFERENCE_DATA.y1}
          stroke={COLORS.reference}
          strokeWidth={3}
          yAxisId="left"
        />
        <ReferenceLine
          y={REFERENCE_DATA.y2}
          stroke={COLORS.reference}
          strokeWidth={3}
          yAxisId="left"
        />
        <Bar
          yAxisId="left"
          dataKey="bloodPressure"
          fill={COLORS.bloodPressure}
          barSize={30}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="pulse"
          stroke={COLORS.pulse}
          dot={{ fill: COLORS.pulse }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default BloodPressureChart;
